import { PLAYBACK_POSITION_DEBOUNCE_MS, type PlaybackRate } from '@/protocol/limits';

/**
 * One active voice player per account, on this device.
 *
 * Section 14: "Voice playback is one active player per account/device; starting
 * another pauses the prior player." That is a device-wide invariant, not a
 * per-component one, so it lives in a module singleton rather than in React
 * state — two `VoicePlayer` components have no way to see each other, and a
 * context provider would still not cover a player mounted in a pinned-messages
 * panel beside one in the timeline.
 *
 * The same object owns the Section 14 auto-advance rule: "Auto-play the next
 * voice note in the currently open chat only while the user keeps playback
 * active; stop on navigation." A queue is registered by the open conversation
 * and cleared when it navigates away, so a player that finishes with no queue
 * simply stops.
 */

export interface PlaybackTarget {
  /** Identifies the message being played; used for the auto-advance queue. */
  key: string;
  /** Pauses this player. Called when another one starts. */
  pause: () => void;
}

type PositionReporter = (key: string, positionMs: number) => void;

class PlaybackManager {
  private active: PlaybackTarget | null = null;
  /** Message keys in timeline order, for auto-advance. */
  private queue: readonly string[] = [];
  private queueOwner: string | null = null;
  private advance: ((key: string) => void) | null = null;
  private reportPosition: PositionReporter | null = null;
  private pendingPositions = new Map<string, number>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Claims playback. Any previously active player is paused first.
   *
   * Returns nothing: the caller starts its own element. Making this return a
   * promise would tempt callers to await it before `play()`, and a `play()` not
   * called synchronously inside the click handler is refused by browser
   * autoplay policy.
   */
  claim(target: PlaybackTarget): void {
    if (this.active && this.active.key !== target.key) {
      this.active.pause();
    }
    this.active = target;
  }

  release(key: string): void {
    if (this.active?.key === key) {
      this.active = null;
    }
  }

  /** Registers the ordered voice messages of the conversation now on screen. */
  setQueue(owner: string, keys: readonly string[], advance: (key: string) => void): void {
    this.queueOwner = owner;
    this.queue = keys;
    this.advance = advance;
  }

  /**
   * Clears the queue when its conversation is left.
   *
   * Section 14 stops auto-advance on navigation. The owner check means a
   * conversation unmounting after another has already mounted cannot clear the
   * new one's queue.
   */
  clearQueue(owner: string): void {
    if (this.queueOwner !== owner) {
      return;
    }
    this.queueOwner = null;
    this.queue = [];
    this.advance = null;
    this.flush();
  }

  /**
   * Called when a voice message finishes.
   *
   * `userInitiated` is false when playback itself started automatically. It is
   * still true here — the chain only continues while the user keeps playback
   * active, and any pause or navigation breaks it — because reaching the end of
   * a note the user chose to play is what authorizes the next one.
   */
  finished(key: string): void {
    this.release(key);
    const index = this.queue.indexOf(key);
    const next = index >= 0 ? this.queue[index + 1] : undefined;
    if (next !== undefined) {
      this.advance?.(next);
    }
  }

  /** Everything stops: a navigation, a sign-out, a page hide. */
  stopAll(): void {
    this.active?.pause();
    this.active = null;
    this.flush();
  }

  /**
   * Records a playback position for synchronization.
   *
   * Section 06 debounces position updates by five seconds. Sending one per
   * `timeupdate` — which fires several times a second — would spend the
   * account's whole mutation budget on a progress bar.
   */
  setPositionReporter(reporter: PositionReporter | null): void {
    this.reportPosition = reporter;
  }

  recordPosition(key: string, positionMs: number): void {
    this.pendingPositions.set(key, positionMs);
    if (this.flushTimer !== null) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, PLAYBACK_POSITION_DEBOUNCE_MS);
  }

  private flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const reporter = this.reportPosition;
    const pending = this.pendingPositions;
    this.pendingPositions = new Map();
    if (!reporter) {
      return;
    }
    for (const [key, position] of pending) {
      reporter(key, position);
    }
  }
}

export const playback = new PlaybackManager();

/** The rate the user last chose, remembered across players within a session. */
let preferredRate: PlaybackRate = 1;

export function getPreferredRate(): PlaybackRate {
  return preferredRate;
}

export function setPreferredRate(rate: PlaybackRate): void {
  preferredRate = rate;
}
